import { useEffect, useState } from "react";
import { Plus, Trash2, Save, X, Edit3 } from "lucide-react";
import { api } from "@/lib/api";
import { SIZES } from "@/lib/pricing";

const EMPTY = { name: "", slug: "", description: "", category: "interior", price: 0, prices: {}, duration_minutes: 30, active: true };

export default function AddonsManager() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/addons", { params: { include_inactive: true } });
    setList(data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setErr("");
    try {
      const sized = Object.fromEntries(
        SIZES.map((z) => [z.v, editing.prices?.[z.v]]).filter(([, v]) => v !== "" && v !== undefined && v !== null)
      );
      const prices = Object.keys(sized).length === SIZES.length ? Object.fromEntries(Object.entries(sized).map(([k, v]) => [k, Number(v)])) : {};
      const payload = { ...editing, prices, price: Number(editing.price), duration_minutes: Number(editing.duration_minutes) };
      if (editing.id) await api.patch(`/addons/${editing.id}`, payload);
      else await api.post("/addons", payload);
      setEditing(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this add-on?")) return;
    await api.delete(`/addons/${id}`);
    await load();
  };

  const input = "w-full bg-[#0f1522] border border-white/10 rounded-xl px-4 py-3 text-slate-200 focus:outline-none focus:border-[#ff6b00]";

  return (
    <div className="mt-12" data-testid="admin-addons">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Add-ons</div>
          <h2 className="font-display mt-2 text-2xl sm:text-3xl font-extrabold uppercase text-slate-100">Paid extras</h2>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="admin-addon-add-new-button" className="cj-btn-ghost px-5 py-2.5 rounded-full inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> New Add-on
        </button>
      </div>

      <div className="mt-5 grid gap-3">
        {list.map((a) => (
          <div key={a.id} className="cj-card p-4 flex flex-wrap items-center gap-4 justify-between" data-testid={`admin-addon-row-${a.slug}`}>
            <div className="min-w-0 flex-1">
              <div className="font-display uppercase font-extrabold text-slate-100">
                {a.name}
                {!a.active && <span className="cj-pill bg-white/5 text-slate-400 border border-white/10 ml-2">Inactive</span>}
              </div>
              <div className="font-mono text-xs text-slate-500 mt-1">
                /{a.slug} · +{a.duration_minutes}m{a.category ? ` · ${a.category}` : ""}
                {Object.keys(a.prices || {}).length > 0 && (
                  <span className="ml-2 text-slate-400">{SIZES.map((z) => `${z.l} $${a.prices[z.v]}`).join(" · ")}</span>
                )}
              </div>
            </div>
            <div className="font-display font-extrabold text-lg text-[#ff6b00]">{Object.keys(a.prices || {}).length > 0 ? "from " : ""}+${a.price.toFixed(0)}</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(a)} data-testid={`admin-addon-edit-${a.slug}`} className="cj-btn-ghost px-3 py-2 rounded-lg text-xs inline-flex items-center gap-1">
                <Edit3 className="h-3.5 w-3.5" /> Edit
              </button>
              <button onClick={() => del(a.id)} data-testid={`admin-addon-delete-${a.slug}`} className="px-3 py-2 rounded-lg text-xs text-red-400 border border-red-500/30 hover:bg-red-500/10 inline-flex items-center gap-1">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-lg bg-[#0b0f17] border border-white/10 rounded-2xl p-6" data-testid="admin-addon-editor">
            <div className="flex items-center justify-between">
              <div className="font-display uppercase font-extrabold text-xl text-slate-100">{editing.id ? "Edit add-on" : "New add-on"}</div>
              <button onClick={() => setEditing(null)} className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center">
                <X className="h-4 w-4 text-slate-200" />
              </button>
            </div>
            <div className="mt-5 grid gap-3">
              <input className={input} placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="addon-name" />
              <input className={input} placeholder="slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="addon-slug" />
              <textarea className={input} placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="addon-desc" />
              <div className="grid grid-cols-2 gap-3">
                <input className={input} type="number" placeholder="Flat price" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="addon-price" />
                <input className={input} type="number" placeholder="Extra minutes" value={editing.duration_minutes} onChange={(e) => setEditing({ ...editing, duration_minutes: e.target.value })} data-testid="addon-duration" />
              </div>
              <select className={input} value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="addon-category">
                <option value="interior">Interior extra</option>
                <option value="exterior">Exterior extra</option>
                <option value="">Other</option>
              </select>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">Size-based prices (optional — fill all four to override flat price)</div>
                <div className="grid grid-cols-4 gap-2">
                  {SIZES.map((z) => (
                    <input
                      key={z.v}
                      className={input}
                      type="number"
                      placeholder={z.l}
                      value={editing.prices?.[z.v] ?? ""}
                      onChange={(e) => setEditing({ ...editing, prices: { ...(editing.prices || {}), [z.v]: e.target.value } })}
                      data-testid={`addon-price-${z.v}`}
                    />
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="addon-active-checkbox" />
                Active (offered at checkout)
              </label>
              {err && <div className="text-sm text-red-400 font-mono">{err}</div>}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="cj-btn-ghost px-5 py-2.5 rounded-full">Cancel</button>
              <button onClick={save} data-testid="admin-addon-save-button" className="cj-btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2">
                <Save className="h-4 w-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
